import { useEffect, useRef } from 'react'
import { subscribeRealtimeSync } from '../lib/realtime.js'

/**
 * Custom React hook for auto-refreshing data in real-time.
 * Listens to BroadcastChannel for instant cross-tab updates, window focus, and periodic polling.
 */
export function useRealtimeSync(onRefresh, { intervalMs = 15000, enabled = true } = {}) {
  const onRefreshRef = useRef(onRefresh)

  useEffect(() => {
    onRefreshRef.current = onRefresh
  }, [onRefresh])

  useEffect(() => {
    if (!enabled) return undefined

    // 1. Subscribe to instant event-driven updates across tabs & windows via BroadcastChannel
    const unsubscribe = subscribeRealtimeSync((detail) => {
      if (typeof onRefreshRef.current === 'function') {
        onRefreshRef.current(detail)
      }
    })

    // 2. Refresh on window focus and visibility change
    const handleFocusOrVisibility = () => {
      if (document.visibilityState === 'visible' && typeof onRefreshRef.current === 'function') {
        onRefreshRef.current({ type: 'focus' })
      }
    }

    window.addEventListener('focus', handleFocusOrVisibility)
    document.addEventListener('visibilitychange', handleFocusOrVisibility)

    // 3. Periodic background polling (default 30s, disabled if intervalMs is 0 or false)
    let intervalId = null
    if (intervalMs && intervalMs > 0) {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible' && typeof onRefreshRef.current === 'function') {
          onRefreshRef.current({ type: 'polling' })
        }
      }, intervalMs)
    }

    return () => {
      unsubscribe()
      window.removeEventListener('focus', handleFocusOrVisibility)
      document.removeEventListener('visibilitychange', handleFocusOrVisibility)
      if (intervalId) clearInterval(intervalId)
    }
  }, [enabled, intervalMs])
}
